import { useCallback, useEffect } from 'react';
import { forEachYield } from 'tradera/utils/scheduler';

const intervals = {};

/**
 * Set up an periodical call to a component callback.
 * Intervals with the same period time are reused.
 * The interval can be cancelled with the returned function. It also cancels itself when no component uses it.
 *
 * @param {function} callback
 * @param {int} period - Milliseconds
 * @returns {function}
 */
export function useTicker(callback, period) {
    // Remove callback from interval and cancel interval if no callbacks remain
    const cleanup = useCallback(() => {
        if (intervals[period]) {
            // eslint-disable-next-line better-mutation/no-mutation
            intervals[period].callbacks = intervals[period].callbacks.filter(
                (cb) => cb !== callback,
            );
            if (intervals[period].callbacks.length === 0) {
                clearInterval(intervals[period].intervalId);
                // eslint-disable-next-line better-mutation/no-mutation
                intervals[period] = undefined;
            }
        }
    }, [callback, period]);

    useEffect(() => {
        if (period <= 0) {
            return;
        }
        // Reuse existing interval
        if (intervals[period]) {
            intervals[period].callbacks.push(callback);
            // Add callback to new interval and save interval id for cancellation
        } else {
            const intervalId = setInterval(async () => {
                forEachYield(intervals[period].callbacks, (callback) =>
                    callback(),
                );
            }, period);
            // eslint-disable-next-line better-mutation/no-mutation
            intervals[period] = { callbacks: [callback], intervalId };
        }

        // Return cancellation callback
        return cleanup;
    }, [callback, cleanup, period]);

    return cleanup;
}
